import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { money } from "../center/ownerCenterUi";

function shortDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(date);
}

function compactMoney(value, currency) {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, notation: "compact", maximumFractionDigits: 1 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Math.round(Number(value || 0)).toLocaleString("es-AR")}`;
  }
}

function niceRange(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  if (min === max) return { min: min - 1, max: max + 1 };
  const pad = Math.max((max - min) * 0.12, Math.max(Math.abs(min), Math.abs(max)) * 0.04, 1);
  return { min: min - pad, max: max + pad };
}

export default function OwnerCashRunwayChart({ rows = [], currency = "ARS" }) {
  const [hovered, setHovered] = React.useState(null);
  const width = 1120;
  const height = 360;
  const left = 78;
  const right = 26;
  const top = 28;
  const bottom = 48;
  const chartW = width - left - right;
  const chartH = height - top - bottom;

  if (!rows.length) {
    return <Box sx={{ minHeight: 300, display: "grid", placeItems: "center", color: "text.secondary", fontWeight: 800 }}>Todavía no hay suficiente información para dibujar la caja futura.</Box>;
  }

  const balances = rows.map((row) => Number(row.balance || 0));
  const range = niceRange(Math.min(0, ...balances), Math.max(0, ...balances));
  const x = (index) => left + (rows.length <= 1 ? chartW / 2 : (index / (rows.length - 1)) * chartW);
  const y = (value) => top + ((range.max - value) / Math.max(1, range.max - range.min)) * chartH;
  const points = rows.map((row, index) => `${x(index)},${y(Number(row.balance || 0))}`).join(" ");
  const zeroY = y(0);
  const ticks = Array.from({ length: 5 }, (_, index) => range.max - ((range.max - range.min) * index) / 4);
  const labelIndexes = [...new Set([0, Math.floor((rows.length - 1) * .25), Math.floor((rows.length - 1) * .5), Math.floor((rows.length - 1) * .75), rows.length - 1])];
  const eventRows = rows.map((row, index) => ({ ...row, index })).filter((row) => (row.events || []).length).slice(0, 18);
  const active = hovered == null ? rows[rows.length - 1] : rows[hovered];

  return (
    <Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Proyección diaria del saldo disponible">
          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={left} x2={width - right} y1={y(tick)} y2={y(tick)} stroke="currentColor" opacity="0.08" />
              <text x={left - 12} y={y(tick) + 4} textAnchor="end" fill="currentColor" opacity="0.58" fontSize="11" fontWeight="700">{compactMoney(tick, currency)}</text>
            </g>
          ))}
          <line x1={left} x2={width - right} y1={zeroY} y2={zeroY} stroke="currentColor" opacity="0.34" strokeDasharray="6 6" />
          <polygon points={`${left},${zeroY} ${points} ${x(rows.length - 1)},${zeroY}`} fill="currentColor" opacity="0.045" />
          <polyline points={points} fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
          {eventRows.map((row) => (
            <circle key={`${row.date}:${row.index}`} cx={x(row.index)} cy={y(Number(row.balance || 0))} r="4.5" fill="currentColor" opacity="0.72" />
          ))}
          {labelIndexes.map((index) => (
            <text key={index} x={x(index)} y={height - 17} textAnchor={index === 0 ? "start" : index === rows.length - 1 ? "end" : "middle"} fill="currentColor" opacity="0.58" fontSize="11" fontWeight="750">{shortDate(rows[index]?.date)}</text>
          ))}
          {rows.map((row, index) => (
            <rect key={row.date} x={x(index) - Math.max(3, chartW / Math.max(rows.length, 1) / 2)} y={top} width={Math.max(6, chartW / Math.max(rows.length, 1))} height={chartH} fill="transparent" onMouseEnter={() => setHovered(index)} onMouseLeave={() => setHovered(null)} />
          ))}
          {hovered != null ? <line x1={x(hovered)} x2={x(hovered)} y1={top} y2={top + chartH} stroke="currentColor" opacity="0.2" strokeDasharray="3 5" /> : null}
        </svg>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mt: .4, px: .4 }}>
        <Box>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 800 }}>{active ? shortDate(active.date) : ""}</Typography>
          <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>{active ? money(active.balance, currency) : "—"}</Typography>
        </Box>
        {active?.events?.length ? (
          <Stack spacing={.2} sx={{ minWidth: 0, maxWidth: 650, alignItems: { sm: "flex-end" } }}>
            {active.events.slice(0, 3).map((event) => (
              <Typography key={event.id} sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 750, textAlign: { sm: "right" } }}>
                {event.direction === "income" ? "+" : "−"}{money(event.amount, currency)} · {event.label}
              </Typography>
            ))}
          </Stack>
        ) : <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 750 }}>Sin vencimientos/cobros puntuales en este día.</Typography>}
      </Stack>
    </Box>
  );
}
